import { StudioNodeStyle } from '../../models/component-studio.models';

export interface StudioMeasureUnitOption {
  value: string;
  label: string;
}

export interface StudioMeasureFieldConfig {
  defaultUnit: string;
  unitOptions: StudioMeasureUnitOption[];
  specialValueOptions?: string[];
  valuePresetsByUnit?: Record<string, string[]>;
  allowNegative?: boolean;
  allowUnitless?: boolean;
  fallbackPresets?: string[];
}

const SPACING_UNIT_OPTIONS: StudioMeasureUnitOption[] = [
  { value: 'px', label: 'px' },
  { value: 'rem', label: 'rem' },
  { value: 'em', label: 'em' },
  { value: '%', label: '%' },
  { value: 'vw', label: 'vw' },
  { value: 'vh', label: 'vh' },
  { value: 'dvw', label: 'dvw' },
  { value: 'dvh', label: 'dvh' }
];

const SIZE_UNIT_OPTIONS: StudioMeasureUnitOption[] = [
  { value: 'px', label: 'px' },
  { value: '%', label: '%' },
  { value: 'rem', label: 'rem' },
  { value: 'em', label: 'em' },
  { value: 'ch', label: 'ch' },
  { value: 'vw', label: 'vw' },
  { value: 'vh', label: 'vh' },
  { value: 'vmin', label: 'vmin' },
  { value: 'vmax', label: 'vmax' },
  { value: 'dvw', label: 'dvw' },
  { value: 'dvh', label: 'dvh' },
  { value: 'svw', label: 'svw' },
  { value: 'svh', label: 'svh' },
  { value: 'lvw', label: 'lvw' },
  { value: 'lvh', label: 'lvh' }
];

const TYPOGRAPHY_UNIT_OPTIONS: StudioMeasureUnitOption[] = [
  { value: 'px', label: 'px' },
  { value: 'rem', label: 'rem' },
  { value: 'em', label: 'em' },
  { value: '%', label: '%' },
  { value: 'ch', label: 'ch' }
];

const LINE_HEIGHT_UNIT_OPTIONS: StudioMeasureUnitOption[] = [
  { value: '', label: 'ratio' },
  { value: '%', label: '%' },
  { value: 'px', label: 'px' },
  { value: 'rem', label: 'rem' },
  { value: 'em', label: 'em' }
];

const POSITION_UNIT_OPTIONS: StudioMeasureUnitOption[] = [
  { value: 'px', label: 'px' },
  { value: '%', label: '%' },
  { value: 'rem', label: 'rem' },
  { value: 'em', label: 'em' },
  { value: 'vw', label: 'vw' },
  { value: 'vh', label: 'vh' },
  { value: 'dvw', label: 'dvw' },
  { value: 'dvh', label: 'dvh' }
];

const SPACING_PRESETS: Record<string, string[]> = {
  px: ['0', '2', '4', '8', '12', '16', '20', '24', '32', '40', '48', '64', '80', '96'],
  rem: ['0', '0.125', '0.25', '0.5', '0.75', '1', '1.25', '1.5', '2', '2.5', '3', '4', '5', '6'],
  em: ['0', '0.125', '0.25', '0.5', '0.75', '1', '1.25', '1.5', '2', '2.5', '3', '4'],
  '%': ['0', '10', '20', '25', '33', '40', '50', '60', '75', '100'],
  vw: ['0', '5', '10', '15', '20', '25', '33', '40', '50', '75', '100'],
  vh: ['0', '5', '10', '15', '20', '25', '33', '40', '50', '75', '100'],
  dvw: ['0', '5', '10', '15', '20', '25', '33', '40', '50', '75', '100'],
  dvh: ['0', '5', '10', '15', '20', '25', '33', '40', '50', '75', '100']
};

const SIZE_PRESETS: Record<string, string[]> = {
  px: ['0', '120', '160', '240', '320', '480', '640', '768', '960', '1180', '1280', '1440'],
  '%': ['0', '25', '33', '50', '66', '75', '100'],
  rem: ['0', '4', '8', '12', '16', '20', '24', '32', '40', '48', '64', '72'],
  em: ['0', '4', '8', '12', '16', '20', '24', '32', '40', '48', '64'],
  ch: ['0', '16', '24', '32', '40', '48', '60', '72', '80'],
  vw: ['0', '10', '20', '25', '33', '40', '50', '60', '75', '100'],
  vh: ['0', '10', '20', '25', '33', '40', '50', '60', '75', '100'],
  vmin: ['0', '10', '20', '25', '33', '40', '50', '60', '75', '100'],
  vmax: ['0', '10', '20', '25', '33', '40', '50', '60', '75', '100'],
  dvw: ['0', '10', '20', '25', '33', '40', '50', '60', '75', '100'],
  dvh: ['0', '10', '20', '25', '33', '40', '50', '60', '75', '100'],
  svw: ['0', '10', '20', '25', '33', '40', '50', '60', '75', '100'],
  svh: ['0', '10', '20', '25', '33', '40', '50', '60', '75', '100'],
  lvw: ['0', '10', '20', '25', '33', '40', '50', '60', '75', '100'],
  lvh: ['0', '10', '20', '25', '33', '40', '50', '60', '75', '100']
};

const FONT_SIZE_PRESETS: Record<string, string[]> = {
  px: ['12', '14', '16', '18', '20', '24', '28', '32', '40', '48', '64'],
  rem: ['0.75', '0.875', '1', '1.125', '1.25', '1.5', '1.75', '2', '2.5', '3', '4'],
  em: ['0.75', '0.875', '1', '1.125', '1.25', '1.5', '1.75', '2', '2.5', '3', '4'],
  '%': ['75', '87.5', '100', '112.5', '125', '150', '175', '200'],
  ch: ['8', '10', '12', '14', '16', '18', '24', '32']
};

const LINE_HEIGHT_PRESETS: Record<string, string[]> = {
  '': ['1', '1.1', '1.2', '1.3', '1.4', '1.5', '1.6', '1.8', '2'],
  '%': ['100', '110', '120', '130', '140', '150', '160', '180', '200'],
  px: ['16', '18', '20', '24', '28', '32', '40'],
  rem: ['1', '1.125', '1.25', '1.5', '1.75', '2', '2.5'],
  em: ['1', '1.125', '1.25', '1.5', '1.75', '2', '2.5']
};

const LETTER_SPACING_PRESETS: Record<string, string[]> = {
  px: ['0', '0.5', '1', '1.5', '2', '3', '4', '6'],
  rem: ['0', '0.015', '0.03', '0.045', '0.06', '0.08', '0.1'],
  em: ['0', '0.015', '0.03', '0.045', '0.06', '0.08', '0.1'],
  '%': ['0', '2', '4', '6', '8', '10', '12']
};

const BORDER_WIDTH_PRESETS: Record<string, string[]> = {
  px: ['0', '1', '2', '3', '4', '6', '8', '12'],
  rem: ['0', '0.0625', '0.125', '0.25', '0.375', '0.5'],
  em: ['0', '0.0625', '0.125', '0.25', '0.375', '0.5']
};

const BORDER_RADIUS_PRESETS: Record<string, string[]> = {
  px: ['0', '4', '8', '12', '16', '20', '24', '32', '48', '999'],
  rem: ['0', '0.25', '0.5', '0.75', '1', '1.25', '1.5', '2', '3', '999'],
  '%': ['0', '10', '20', '25', '30', '40', '50']
};

const POSITION_PRESETS: Record<string, string[]> = {
  px: ['-96', '-64', '-32', '-16', '-8', '0', '8', '16', '32', '64', '96'],
  '%': ['-100', '-50', '-25', '0', '25', '50', '75', '100'],
  rem: ['-8', '-4', '-2', '-1', '-0.5', '0', '0.5', '1', '2', '4', '8'],
  em: ['-8', '-4', '-2', '-1', '-0.5', '0', '0.5', '1', '2', '4', '8'],
  vw: ['-100', '-50', '-25', '0', '25', '50', '75', '100'],
  vh: ['-100', '-50', '-25', '0', '25', '50', '75', '100'],
  dvw: ['-100', '-50', '-25', '0', '25', '50', '75', '100'],
  dvh: ['-100', '-50', '-25', '0', '25', '50', '75', '100']
};

export const STUDIO_MEASURE_FIELD_CONFIGS: Partial<Record<keyof StudioNodeStyle, StudioMeasureFieldConfig>> = {
  gap: {
    defaultUnit: 'rem',
    unitOptions: SPACING_UNIT_OPTIONS,
    valuePresetsByUnit: SPACING_PRESETS
  },
  width: {
    defaultUnit: '%',
    unitOptions: SIZE_UNIT_OPTIONS,
    specialValueOptions: ['auto', 'fit-content', 'min-content', 'max-content'],
    valuePresetsByUnit: SIZE_PRESETS
  },
  height: {
    defaultUnit: 'px',
    unitOptions: SIZE_UNIT_OPTIONS,
    specialValueOptions: ['auto', 'fit-content', 'min-content', 'max-content'],
    valuePresetsByUnit: SIZE_PRESETS
  },
  minWidth: {
    defaultUnit: 'px',
    unitOptions: SIZE_UNIT_OPTIONS,
    specialValueOptions: ['auto', 'fit-content', 'min-content', 'max-content'],
    valuePresetsByUnit: SIZE_PRESETS
  },
  maxWidth: {
    defaultUnit: '%',
    unitOptions: SIZE_UNIT_OPTIONS,
    specialValueOptions: ['none', 'fit-content', 'min-content', 'max-content'],
    valuePresetsByUnit: SIZE_PRESETS
  },
  minHeight: {
    defaultUnit: '%',
    unitOptions: SIZE_UNIT_OPTIONS,
    specialValueOptions: ['auto', 'fit-content', 'min-content', 'max-content'],
    valuePresetsByUnit: SIZE_PRESETS
  },
  maxHeight: {
    defaultUnit: 'px',
    unitOptions: SIZE_UNIT_OPTIONS,
    specialValueOptions: ['none', 'fit-content', 'min-content', 'max-content'],
    valuePresetsByUnit: SIZE_PRESETS
  },
  flexBasis: {
    defaultUnit: '%',
    unitOptions: SIZE_UNIT_OPTIONS,
    specialValueOptions: ['auto', 'content', 'fit-content', 'min-content', 'max-content'],
    valuePresetsByUnit: SIZE_PRESETS
  },
  padding: {
    defaultUnit: 'rem',
    unitOptions: SPACING_UNIT_OPTIONS,
    valuePresetsByUnit: SPACING_PRESETS
  },
  margin: {
    defaultUnit: 'rem',
    unitOptions: SPACING_UNIT_OPTIONS,
    specialValueOptions: ['auto'],
    valuePresetsByUnit: SPACING_PRESETS,
    allowNegative: true
  },
  fontSize: {
    defaultUnit: 'rem',
    unitOptions: TYPOGRAPHY_UNIT_OPTIONS,
    valuePresetsByUnit: FONT_SIZE_PRESETS
  },
  lineHeight: {
    defaultUnit: '',
    unitOptions: LINE_HEIGHT_UNIT_OPTIONS,
    specialValueOptions: ['normal'],
    valuePresetsByUnit: LINE_HEIGHT_PRESETS,
    allowUnitless: true
  },
  letterSpacing: {
    defaultUnit: 'px',
    unitOptions: TYPOGRAPHY_UNIT_OPTIONS.filter((option) => option.value !== 'ch'),
    valuePresetsByUnit: LETTER_SPACING_PRESETS,
    allowNegative: true
  },
  borderWidth: {
    defaultUnit: 'px',
    unitOptions: TYPOGRAPHY_UNIT_OPTIONS.filter((option) => option.value !== '%' && option.value !== 'ch'),
    valuePresetsByUnit: BORDER_WIDTH_PRESETS
  },
  outlineWidth: {
    defaultUnit: 'px',
    unitOptions: TYPOGRAPHY_UNIT_OPTIONS.filter((option) => option.value !== '%' && option.value !== 'ch'),
    valuePresetsByUnit: BORDER_WIDTH_PRESETS
  },
  borderRadius: {
    defaultUnit: 'rem',
    unitOptions: [
      { value: 'px', label: 'px' },
      { value: 'rem', label: 'rem' },
      { value: '%', label: '%' }
    ],
    valuePresetsByUnit: BORDER_RADIUS_PRESETS
  },
  top: {
    defaultUnit: 'px',
    unitOptions: POSITION_UNIT_OPTIONS,
    valuePresetsByUnit: POSITION_PRESETS,
    allowNegative: true
  },
  right: {
    defaultUnit: 'px',
    unitOptions: POSITION_UNIT_OPTIONS,
    valuePresetsByUnit: POSITION_PRESETS,
    allowNegative: true
  },
  bottom: {
    defaultUnit: 'px',
    unitOptions: POSITION_UNIT_OPTIONS,
    valuePresetsByUnit: POSITION_PRESETS,
    allowNegative: true
  },
  left: {
    defaultUnit: 'px',
    unitOptions: POSITION_UNIT_OPTIONS,
    valuePresetsByUnit: POSITION_PRESETS,
    allowNegative: true
  }
};
